import axios from "axios";
import { atom, useAtom } from "jotai";
import { SetStateAction } from "jotai/core/types";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../shared/api/yenta-api";
import { handleGoogleDriveImageUrl } from "../shared/utils";

import {
  PuzzleLoadedImage,
  PuzzleRoundStatusEnum,
  PuzzleRoundType,
} from "./widget.types";

export const puzzleRoundAtom = atom<
  PuzzleRoundType,
  SetStateAction<PuzzleRoundType>
>(
  async () => {
    try {
      const {
        data: { rounds },
      }: { data: { rounds: PuzzleRoundType[] } } = await axios.get(
        `${
          window.FKL.yentaServicesApiUrl
        }/puzzle/${window.FKL.puzzleSlug.trim()}`
      );

      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

      const roundIndex =
        rounds.findIndex(
          (round) => round.id === submission?.data.lastPuzzleRoundId
        ) + 1;
      const round = rounds[roundIndex] ?? rounds[0];

      const slicesPerRow = 3;
      const totalRows = 3;
      const totalSlices = slicesPerRow * totalRows;
      const scaleFactor = 0.96;

      const pendingSlices = Array.from({ length: totalSlices })
        .map((_, i) => i)
        .map<[number, number]>((a) => [Math.random(), a])
        .sort((a, b) => a[0] - b[0])
        .map((a) => a[1]);

      const arrangedSlices = Array.from({ length: totalSlices }).map(
        () => null
      );

      const loadedPuzzleImage: PuzzleLoadedImage = await new Promise(
        (resolve) => {
          const img = new Image();
          img.src = handleGoogleDriveImageUrl(round.puzzleImage);
          img.onload = () =>
            resolve({
              puzzleImage: img.src,
              puzzleOriginalImageWidth: img.width,
              puzzleOriginalImageHeight: img.height,
            });
        }
      );

      return {
        ...round,
        allTimePoints: Number(submission?.data.quizExtraEntries) || 0,
        initialPuzzleSlicesCompleted:
          Number(submission?.data.puzzleSlicesCompleted) || 0,
        logoUrl: handleGoogleDriveImageUrl(round.logoUrl),
        ...loadedPuzzleImage,
        cpl: {
          ...round.cpl,
          imageUrl: round.cpl.imageUrl
            ? handleGoogleDriveImageUrl(round.cpl.imageUrl)
            : "",
        },
        slicesPerRow,
        totalRows,
        totalSlices,
        pendingSlices,
        arrangedSlices,
        scaleFactor,
        roundIndex,
      };
    } catch (error) {
      throw new Error(error.response?.data?.message ?? error.message);
    }
  },
  (get, set, update) => {
    const newValue =
      typeof update === "function" ? update(get(puzzleRoundAtom)) : update;
    set(puzzleRoundAtom, newValue);
  }
);

export function usePuzzle() {
  const [status, setStatus] = useState<PuzzleRoundStatusEnum>(
    PuzzleRoundStatusEnum.IDLE
  );
  const [data, setData] = useAtom(puzzleRoundAtom);
  const handleWidgetError = useErrorHandler();

  const scaledImage = useScaledImage({
    slicesPerRow: data.slicesPerRow,
    totalRows: data.totalRows,
    totalSlices: data.totalSlices,
  });

  const correctTiles = useMemo(
    () => data.arrangedSlices.filter((slice, i) => slice === i).length,
    [data.arrangedSlices]
  );

  const handleNextRound = useCallback(
    (isAccepted: boolean) => {
      setData((data) => ({
        ...data,
        cpl: {
          ...data.cpl,
          isAccepted,
        },
      }));
    },
    [setData]
  );

  const handleStartGame = useCallback(() => {
    setStatus(PuzzleRoundStatusEnum.STARTED);
  }, []);

  useEffect(() => {
    if (correctTiles === data.totalSlices) {
      setStatus(PuzzleRoundStatusEnum.WON);
    }
  }, [correctTiles, data.totalSlices]);

  const handleTimeout = useCallback(() => {
    setStatus(PuzzleRoundStatusEnum.LOST);
  }, []);

  useEffect(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    if (submission) {
      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") ?? "";

      const patchData = {
        ...submission.data,
        quizExtraEntries: data.allTimePoints + correctTiles,
        puzzleSlicesCompleted: data.initialPuzzleSlicesCompleted + correctTiles,
        quizAcceptedCpls: Array.from(
          new Set(
            [
              ...((submission.data.quizAcceptedCpls as string) ?? "").split(
                ","
              ),
              data.cpl.isAccepted && data.cpl.name,
            ].filter(Boolean)
          )
        ).join(","),
      };

      if (
        status === PuzzleRoundStatusEnum.WON ||
        status === PuzzleRoundStatusEnum.LOST
      ) {
        (patchData as any).lastPuzzleRoundId = data.id;
      }

      updateFormioSubmission(
        sweepFormUrl,
        {
          ...submission,
          data: patchData,
        },
        handleWidgetError
      )
        .then((submission: any) => {
          window.theme.Sweepstake.prototype.storeSubmission(submission);
        })
        .then(() => {
          if (data.cpl.isAccepted === true || data.cpl.isAccepted === false) {
            window.location.reload();
          }
        });
    }
  }, [
    correctTiles,
    data.allTimePoints,
    data.cpl.isAccepted,
    data.cpl.name,
    data.id,
    status,
    data.initialPuzzleSlicesCompleted,
    handleWidgetError,
  ]);

  return {
    status,
    data: { ...data, correctTiles },
    scaledImage,
    setData,
    handleStartGame,
    handleTimeout,
    handleNextRound,
  };
}

function useScaledImage({
  slicesPerRow,
  totalRows,
  totalSlices,
}: {
  slicesPerRow: number;
  totalRows: number;
  totalSlices: number;
}) {
  const [windowWidth, setWindowWidth] = useState(0);

  const baseWindowWidth = 1280;
  const maxWidth = 375;
  const minWidth = 160;
  const heightRatio = 1;

  useLayoutEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const imageWidth = useMemo(
    () =>
      Math.max(
        Math.min((windowWidth / baseWindowWidth) * maxWidth, maxWidth),
        minWidth
      ),
    [baseWindowWidth, windowWidth]
  );

  const imageHeight = useMemo(() => imageWidth * heightRatio, [
    imageWidth,
    heightRatio,
  ]);

  const sliceWidth = useMemo(() => imageWidth / slicesPerRow, [
    imageWidth,
    slicesPerRow,
  ]);

  const sliceHeight = useMemo(() => imageHeight / totalRows, [
    imageHeight,
    totalRows,
  ]);

  const slices = useMemo(
    () =>
      Array.from({ length: totalSlices }).map((_, i) => ({
        x: (i % slicesPerRow) * sliceWidth * -1,
        y: ~~(i / slicesPerRow) * sliceHeight * -1,
      })),
    [sliceHeight, sliceWidth, slicesPerRow, totalSlices]
  );

  return {
    windowWidth,
    imageWidth,
    imageHeight,
    sliceWidth,
    sliceHeight,
    slices,
  };
}
